<template>
  <div
    v-if="hasDisabledFeatures"
    :style="{ display: 'flex', background: $colors.yellow.lighten4, border: `1px solid ${$colors.yellow.base}`, alignItems: 'center', padding: '.8rem 1.4rem', borderRadius: '4px', marginBottom: '2rem' }"
    class="_key-hint"
  >
    <div :style="{ flex: '1 1 auto' }">
      <div :style="{ display: 'flex' }">
        <div
          class="_icon"
          :style="{ marginRight: '1rem', marginTop: '.4rem' }"
        >
          <c-icon
            type="warning"
            :color="$colors.yellow.darken2"
          />
        </div>
        <div v-if="severalFeaturesDisabled">
          The following features are disabled. Upgrade to use them:
          <ul :style="{ marginBlockStart: '.2rem', paddingInlineStart: '2rem', marginBlockEnd: 0 }">
            <li>
              Screenshots and images (no storage space left)
            </li>
            <li>
              AI prefills for properties
            </li>
            <li>
              Page blocks
            </li>
          </ul>
        </div>
        <template v-else-if="!hasStorage && usesStorage">
          Screenshots are disabled because your storage space is used up. Upgrade to
          increase your storage limit.
        </template>
        <template v-else>
          AI prefills and page blocks are not available in the free plan. Upgrade to
          use those features.
        </template>
      </div>
      <div :style="{ marginTop: '1rem' }">
        <c-button
          :href="upgradeUrl"
          target="_blank"
          outlined
        >
          Upgrade now
        </c-button>
      </div>
    </div>
  </div>
</template>

<script setup>
import CButton from 'shared/components/CButton.vue';
import CIcon from 'shared/components/CIcon.vue';
import useFeatureAccess from 'shared/services/feature-access';
import useSubscription from 'shared/services/subscription';
import { BETA_PHASE_MANAGER, USER_SERVICE, featureFlags } from 'shared/constants';
import { computed, inject } from 'vue';

const userService = inject(USER_SERVICE);
const betaPhaseManager = inject(BETA_PHASE_MANAGER);
const { currentPlan, subscriptionPastDue, hasStorage } = useSubscription(userService.currentUser, betaPhaseManager);
const { hasFeatureAccess } = useFeatureAccess(currentPlan, subscriptionPastDue, betaPhaseManager);
const canUseBlocks = computed(() => hasFeatureAccess(featureFlags.pageBlocks));
const canUseAIProperties = computed(() => hasFeatureAccess(featureFlags.aiProperties));
const hasDisabledFeatures = computed(() => (!canUseAIProperties.value) || (!canUseBlocks.value) || (!hasStorage.value && props.usesStorage));
const upgradeUrl = `${import.meta.env.VITE_WEBSITE_URL}/app/plans`;

// eslint-disable-next-line no-undef
const props = defineProps({
  usesBlocks: {
    type: Boolean,
    default: false,
  },
  usesAIPrefills: {
    type: Boolean,
    default: false,
  },
  usesStorage: {
    type: Boolean,
    default: false,
  },
});

const severalFeaturesDisabled = computed(() => {
  let count = 0;
  if (!hasStorage.value) {
    count += 1;
  }
  if (!canUseBlocks.value && props.usesBlocks) {
    count += 1;
  }
  if (!canUseAIProperties.value && props.usesAIPrefills) {
    count += 1;
  }
  return count > 1;
});
</script>

<style scoped lang="scss">

</style>
